// The SITE's clock. A site has one calendar and one wall clock — its own zone's —
// and every surface reads time through them, whatever zone the viewer's browser
// or phone is in: a presenter in Tokyo looking at a New York site sees New York's
// "today" and New York's timestamps, on every page alike.
import { addDays } from '../activities/model/days.js';
import { dateTimeFormat } from './dateTimeFormat.js';

/** Today on the site's calendar, as 'YYYY-MM-DD' (at `now`, default the current
 *  instant). A site east of the viewer has already rolled over. */
export function projectToday(tz: string, now: number = Date.now()): string {
  return dateTimeFormat('en-CA', { timeZone: tz }).format(now);
}

/** The zone's wall clock at `epochMs`, read back as though it were UTC. Its
 *  distance from `epochMs` is the zone's UTC offset at that instant. */
function wallClockAsUtc(tz: string, epochMs: number): number {
  const parts = dateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(epochMs);
  const fields = new Map(parts.map((p) => [p.type, p.value]));
  const field = (type: Intl.DateTimeFormatPartTypes): number => Number(fields.get(type));
  return Date.UTC(field('year'), field('month') - 1, field('day'), field('hour'), field('minute'), field('second'));
}

/** The instant the site's wall clock reads `hh`:00 on `day`. The offset is read
 *  twice: once at the UTC guess, then at the first estimate, so a DST change
 *  falling between the two still lands on the right instant. */
function siteWallTimeMs(day: string, tz: string, hh: '00' | '12'): number {
  const asUtc = Date.parse(`${day}T${hh}:00:00Z`);
  const estimate = asUtc - (wallClockAsUtc(tz, asUtc) - asUtc);
  return asUtc - (wallClockAsUtc(tz, estimate) - estimate);
}

/** Milliseconds from `now` until the site's calendar rolls over to the next day
 *  — what a "today" that must stay right across midnight waits for. */
export function msUntilNextSiteDay(tz: string, now: number = Date.now()): number {
  return siteWallTimeMs(addDays(projectToday(tz, now), 1), tz, '00') - now;
}

// The date-picker convention: a picker holds a zone-naive Date whose LOCAL
// fields are the site's calendar day, while the stored value is an instant.
// Converting at both ends keeps "picked Oct 20" from being stored as the
// viewer's midnight and read back as Oct 19 on a site in another zone.

/** A picked day (the Date's local y/m/d) as noon of that day on the site's
 *  calendar, in epoch ms. Noon, not midnight, so no DST shift moves the day. */
export function pickerDateToProjectNoonMs(picked: Date, tz: string): number {
  const ymd = [picked.getFullYear(), picked.getMonth() + 1, picked.getDate()]
    .map((n, i) => String(n).padStart(i === 0 ? 4 : 2, '0'))
    .join('-');
  return siteWallTimeMs(ymd, tz, '12');
}

/** Inverse of `pickerDateToProjectNoonMs`: the site-calendar day a stored
 *  instant falls on, as a zone-naive picker Date (local midnight of that day). */
export function projectDayAsPickerDate(instant: string | Date, tz: string): Date {
  const day = projectToday(tz, new Date(instant).getTime());
  return new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

/** An instant on the site's wall clock — the ONE absolute timestamp pattern
 *  (date + time, the viewer's language) shared by the task log, checklist
 *  evidence, task comments and issue comments, on web and mobile. */
export function formatSiteDateTime(at: string | number | Date, tz: string, lang: string): string {
  return dateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', timeZone: tz }).format(new Date(at));
}

export interface ISiteStamp {
  /** What the row shows: relative within a day ("5 minutes ago"), else absolute. */
  text: string;
  /** Always the absolute site time — for a `title`, so a relative stamp can be
   *  pinned to its moment on hover. */
  title: string;
}

/** A conversation stamp: relative while recent, the site's absolute time after. */
export function siteStamp(at: string, tz: string, lang: string, now: number = Date.now()): ISiteStamp {
  const title = formatSiteDateTime(at, tz, lang);
  const diffMin = Math.round((now - Date.parse(at)) / 60_000);
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) >= 24) return { text: title, title };
  const relative = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const text = Math.abs(diffMin) < 60 ? relative.format(-diffMin, 'minute') : relative.format(-diffH, 'hour');
  return { text, title };
}
